
import React, { useState } from 'react';
import { PrayerList } from '../types';
import { FolderIcon, PlusIcon, CloseIcon, DeleteIcon } from './icons';

interface SidebarProps {
  lists: PrayerList[];
  activeListId: string;
  onSelectList: (id: string) => void;
  onAddList: (name: string) => void;
  onDeleteList: (id: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ lists, activeListId, onSelectList, onAddList, onDeleteList, isOpen, onClose }) => {
  const [newListName, setNewListName] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleAddList = () => {
    if (newListName.trim()) {
      onAddList(newListName.trim());
      setNewListName('');
      setIsAdding(false);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
      if(e.key === 'Enter') {
          handleAddList();
      } else if (e.key === 'Escape') {
          setIsAdding(false);
          setNewListName('');
      }
  }

  return (
    <aside className={`fixed inset-y-0 left-0 z-30 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full shadow-md transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:flex-shrink-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Prayer Companion</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Voice-Powered Prayer Journal</p>
        </div>
        <button onClick={onClose} className="md:hidden p-1 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">
            <CloseIcon className="w-6 h-6" />
        </button>
      </div>
      <nav className="flex-1 py-4 px-2 space-y-1">
        <button
          onClick={() => onSelectList('all')}
          className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
            activeListId === 'all'
              ? 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
          }`}
        >
          <FolderIcon className="w-5 h-5" />
          <span>All Prayers</span>
        </button>
        {lists.map(list => (
          <div 
            key={list.id} 
            className={`group flex items-center justify-between gap-3 px-4 py-2.5 text-sm font-medium rounded-md transition-colors cursor-pointer ${
              activeListId === list.id
                ? 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
            onClick={() => onSelectList(list.id)}
          >
            <div className="flex items-center gap-3 overflow-hidden">
                <FolderIcon className="w-5 h-5 flex-shrink-0" />
                <span className="truncate">{list.name}</span>
            </div>
            {list.id !== '1' && ( // Don't allow deleting the "General" list
              <button 
                  onClick={(e) => {
                      e.stopPropagation();
                      onDeleteList(list.id);
                  }}
                  className="p-1 text-gray-400 dark:text-gray-500 rounded-full opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-red-500 dark:hover:text-red-400 transition-opacity focus:opacity-100"
                  aria-label={`Delete ${list.name} list`}
              >
                  <DeleteIcon className="w-4 h-4"/>
              </button>
            )}
          </div>
        ))}
      </nav>
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        {isAdding ? (
            <div className="relative">
                <input
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={() => setIsAdding(false)}
                    placeholder="New list name..."
                    className="w-full pl-3 pr-10 py-2 text-sm bg-white text-gray-900 placeholder-gray-500 border-indigo-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                    autoFocus
                />
                 <button onClick={handleAddList} className="absolute inset-y-0 right-0 px-3 flex items-center text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300">
                    <PlusIcon className="w-5 h-5" />
                </button>
            </div>
        ) : (
            <button
            onClick={() => setIsAdding(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 rounded-md hover:bg-indigo-200 dark:hover:bg-indigo-900/60 transition-colors"
            >
            <PlusIcon className="w-5 h-5" />
            <span>New List</span>
            </button>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
